import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AppContext from '../../../Context/AppContext';
import currentUserStep from '../../../util/currentUserStep';
import {
    enablePortalV2Session,
    resolveNonSubscriberPathV2,
    resolveUserStepPathV2,
} from '../../../util/portalV2';
import oolaLogo from '../../../Assets/oola_w.svg';
import oolaBusLogin from '../../../Assets/img/PortalV2/oola-bus-login.png';
import '../portalV2Shared.css';
import './LoginV2.css';

/**
 * Portal V2 login — parallel to classic LoginHeader modal.
 * Route: /login-v2
 * Always continues into the V2 journey after auth.
 */
const LoginV2 = () => {
    const { login, checkUserSession } = useContext(AppContext);
    const history = useHistory();
    const formRef = useRef(null);
    const userIdRef = useRef(null);

    const [loginType, setLoginType] = useState(2);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [focused, setFocused] = useState('');

    useEffect(() => {
        enablePortalV2Session();
        const session = checkUserSession();
        if (session?.token) {
            history.replace(resolveUserStepPathV2(session.userStepId));
        }
    }, [checkUserSession, history]);

    useEffect(() => {
        const t = window.setTimeout(() => {
            if (userIdRef.current) userIdRef.current.focus();
        }, 450);
        return () => window.clearTimeout(t);
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        const formData = new FormData(formRef.current);
        let userId = String(formData.get('userId') || '').trim();
        const password = String(formData.get('password') || '');
        if (!userId || !password) {
            setError('Enter your credentials to continue.');
            return;
        }

        if (loginType === 1) {
            userId = userId.replace(/\D/g, '');
            if (userId.length < 10) {
                setError('Enter a valid 10-digit phone number.');
                return;
            }
        }

        setBusy(true);
        let result;
        try {
            const loginPromise = login(userId, password, loginType);
            const timeoutPromise = new Promise((_, reject) => {
                window.setTimeout(
                    () =>
                        reject(
                            new Error(
                                'Login is taking too long. Check the API/database connection and try again.'
                            )
                        ),
                    25000
                );
            });
            result = await Promise.race([loginPromise, timeoutPromise]);
        } catch (err) {
            setError(
                err?.message ||
                    'Unable to reach the server. Check your connection and try again.'
            );
            setBusy(false);
            return;
        }
        setBusy(false);

        if (typeof result === 'number') {
            enablePortalV2Session();
            if (result === -1) {
                history.push(resolveNonSubscriberPathV2());
            } else {
                history.push(resolveUserStepPathV2(result));
            }
            return;
        }

        if (result?.data) {
            const msg =
                result.data.message ||
                result.data.Message ||
                'Login failed. Please try again.';
            setError(msg);
            if (result.data.severity === 3) {
                setLoginType(2);
            }
            return;
        }

        setError('Login failed. Please try again.');
    };

    return (
        <div
            className="pv2 pv2-login"
            style={{ '--pv2-login-bus': `url(${oolaBusLogin})` }}
        >
            <div className="pv2-login__atmosphere" aria-hidden>
                <span className="pv2-login__orb" />
                <span className="pv2-login__scrim" />
            </div>

            <header className="pv2-login__header">
                <img
                    src={oolaLogo}
                    alt="Oola"
                    className="pv2-login__logo"
                />
            </header>

            <div className="pv2-login__stage">
                <aside className="pv2-login__brand">
                    <h1 className="pv2-login__headline">
                        <span>Oola</span>
                        <span>Lifestyle</span>
                        <span>Framework</span>
                    </h1>
                    <p className="pv2-login__lead">
                        Live a life of balance, growth, and purpose
                    </p>
                </aside>

                <form
                    ref={formRef}
                    className="pv2-login__card"
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div className="pv2-login__card-head">
                        <p className="pv2-eyebrow">Welcome back</p>
                        <h2 className="pv2-login__card-title">
                            Access your programs
                        </h2>
                        <p className="pv2-login__card-sub">
                            Use your phone number or Customer Id to continue.
                        </p>
                    </div>

                    <div className="pv2-login__tabs" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={loginType === 1}
                            className={`pv2-login__tab${
                                loginType === 1 ? ' is-active' : ''
                            }`}
                            onClick={() => setLoginType(1)}
                        >
                            Phone
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={loginType === 2}
                            className={`pv2-login__tab${
                                loginType === 2 ? ' is-active' : ''
                            }`}
                            onClick={() => setLoginType(2)}
                        >
                            Customer Id
                        </button>
                    </div>

                    {error ? (
                        <p className="pv2-error" role="alert">
                            {error}
                        </p>
                    ) : null}

                    <label
                        className={`pv2-login__field${
                            focused === 'userId' ? ' is-focused' : ''
                        }`}
                    >
                        <span>
                            {loginType === 1 ? 'Phone number' : 'Customer Id'}
                        </span>
                        <input
                            ref={userIdRef}
                            className="pv2-input"
                            name="userId"
                            autoComplete="username"
                            placeholder={
                                loginType === 1
                                    ? 'Enter phone number'
                                    : 'Customer Id (any local id works)'
                            }
                            disabled={busy}
                            onFocus={() => setFocused('userId')}
                            onBlur={() => setFocused('')}
                        />
                    </label>

                    <label
                        className={`pv2-login__field${
                            focused === 'password' ? ' is-focused' : ''
                        }`}
                    >
                        <span>Password</span>
                        <input
                            className="pv2-input"
                            name="password"
                            type="password"
                            autoComplete="current-password"
                            placeholder="Enter password"
                            disabled={busy}
                            onFocus={() => setFocused('password')}
                            onBlur={() => setFocused('')}
                        />
                    </label>

                    <button
                        type="submit"
                        className="pv2-btn pv2-btn--primary pv2-login__submit"
                        disabled={busy}
                    >
                        {busy ? 'Signing in…' : 'Sign in'}
                        {!busy ? <span aria-hidden>→</span> : null}
                    </button>

                    <nav className="pv2-login__footer" aria-label="Account help">
                        <a
                            className="pv2-login__footer-link"
                            href="https://www.usana.com/ux/cart/en-US/product/OOLA-FG-00036"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Sign up
                        </a>
                        <a
                            className="pv2-login__footer-link"
                            href="https://sso.usana.com/enu-US/forgot"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Reset password
                        </a>
                    </nav>
                </form>
            </div>
        </div>
    );
};

export default LoginV2;
